// Core
import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { FieldSearchSelect, FieldSelect, RemoteTable } from "components/AtomsV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetBilateralEnergyAllocationsByCommunityIdPaginationQuery } from "state/bilateral_energy_allocations/bilateralEnergyAllocationsApiSlice";

// Utils
import { useGetMeters } from "utils/crud/read";
import { datetimeToDate, datetimeToTime } from "utils/time";

const ROUND_DECIMALS = 3;

// numeric cell renderer: tabular, right-aligned, fixed to 3 decimals
const num = (decimals) => (color) => (v) =>
  (
    <span className="tabular-nums" style={{ color, fontWeight: 600 }}>
      {v != null ? Number(v).toFixed(decimals) : "—"}
    </span>
  );

const cpe = (v) => <span className="font-mono text-[12.5px] text-ink">{v}</span>;

function ListBilateralEnergyAllocations({ setIsLoading }) {
  const community = useSelector(selectCurrentCommunity);
  const meters = useGetMeters("true");
  const { t } = useTranslation(["community"]);

  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [providerMeterId, setProviderMeterId] = useState(null);
  const [receiverMeterId, setReceiverMeterId] = useState(null);

  // READ Bilateral Energy Allocations (server-side pagination / filtering / sorting)
  const { data, isFetching, isLoading } =
    useGetBilateralEnergyAllocationsByCommunityIdPaginationQuery(
      {
        page,
        limit,
        date,
        time,
        providerMeterId,
        receiverMeterId,
        communityId: community.id,
        sortColumn: sortOrder?.column,
        sortDirection: sortOrder?.direction,
      },
      {
        pollingInterval: 15000,
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true,
      }
    );

  useEffect(() => {
    setIsLoading(isLoading || isFetching);
  }, [isLoading, isFetching]);

  const meterIdToIdentifier = (meterId) => meters?.find((m) => m.id === meterId)?.identifier ?? "—";

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((row, i) => ({
        id: `${row.datetime_timestamp}-${row.provider_meter_id}-${row.receiver_meter_id}-${i}`,
        date: datetimeToDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        provider_meter_id: row.provider_meter_id ? meterIdToIdentifier(row.provider_meter_id) : "—",
        receiver_meter_id: row.receiver_meter_id ? meterIdToIdentifier(row.receiver_meter_id) : "—",
        energy: row.energy,
        coefficient_value: row.coefficient_value,
      })),
    [data, meters]
  );

  const value = num(ROUND_DECIMALS);

  const columns = [
    {
      key: "date",
      label: t("bilateralEnergyAllocations.table.columns.date"),
      sortable: true,
      width: "13%",
    },
    {
      key: "time",
      label: t("bilateralEnergyAllocations.table.columns.time"),
      sortable: true,
      width: "12%",
    },
    {
      key: "provider_meter_id",
      label: t("bilateralEnergyAllocations.table.columns.provider_meter"),
      width: "20%",
      render: cpe,
    },
    {
      key: "receiver_meter_id",
      label: t("bilateralEnergyAllocations.table.columns.receiver_meter"),
      width: "20%",
      render: cpe,
    },
    {
      key: "energy",
      label: t("bilateralEnergyAllocations.table.columns.energy"),
      align: "right",
      render: value("var(--accent)"),
    },
    {
      key: "coefficient_value",
      label: t("bilateralEnergyAllocations.table.columns.coefficient_value"),
      align: "right",
      render: value("var(--warm)"),
    },
  ];

  const dateOptions = [
    { value: "", label: t("bilateralEnergyAllocations.table.filters.all_dates") },
    ...(data?.distinct_dates ?? []).map((o) => ({ value: o.date, label: o.date })),
  ];
  const timeOptions = [
    { value: "", label: t("bilateralEnergyAllocations.table.filters.all_times") },
    ...(data?.distinct_times ?? []).map((o) => ({ value: o.time, label: o.time })),
  ];
  const meterOptions = (allMetersLabel) => [
    { value: "", label: allMetersLabel },
    ...(meters ?? []).map((m) => ({ value: m.identifier, label: m.identifier })),
  ];

  const resetPage = () => setPage(0);
  const onDate = (v) => {
    resetPage();
    setDate(v || null);
  };
  const onTime = (v) => {
    resetPage();
    setTime(v || null);
  };
  const onProvider = (identifier) => {
    resetPage();
    setProviderMeterId(meters?.find((m) => m.identifier === identifier)?.id ?? null);
  };
  const onReceiver = (identifier) => {
    resetPage();
    setReceiverMeterId(meters?.find((m) => m.identifier === identifier)?.id ?? null);
  };

  const filters = (
    <>
      <div className="min-w-[170px]">
        <FieldSelect value={date ?? ""} options={dateOptions} onChange={onDate} />
      </div>
      <div className="min-w-[150px]">
        <FieldSelect value={time ?? ""} options={timeOptions} onChange={onTime} />
      </div>
      <div className="min-w-[200px]">
        <FieldSearchSelect
          value={meters?.find((m) => m.id === providerMeterId)?.identifier ?? ""}
          onChange={onProvider}
          options={meterOptions(t("bilateralEnergyAllocations.table.filters.all_providers"))}
        />
      </div>
      <div className="min-w-[200px]">
        <FieldSearchSelect
          value={meters?.find((m) => m.id === receiverMeterId)?.identifier ?? ""}
          onChange={onReceiver}
          options={meterOptions(t("bilateralEnergyAllocations.table.filters.all_receivers"))}
        />
      </div>
    </>
  );

  return (
    <div className="rec-card">
      <RemoteTable
        columns={columns}
        rows={rows}
        totalCount={data?.total_count ?? 0}
        page={page}
        limit={limit}
        onPageChange={setPage}
        sort={sortOrder}
        onSortChange={setSortOrder}
        filters={filters}
        loading={isFetching}
      />
    </div>
  );
}

ListBilateralEnergyAllocations.defaultProps = {
  setIsLoading: () => {},
};

ListBilateralEnergyAllocations.propTypes = {
  setIsLoading: PropTypes.func,
};

export default ListBilateralEnergyAllocations;
